import {Component, effect, inject, input, output, signal, ChangeDetectionStrategy} from "@angular/core"
import {MatButtonToggleModule} from "@angular/material/button-toggle"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {ToggleComponent} from "@app/common/components/buttons/toggle/toggle.component"
import {ListItemComponent} from "@app/common/components/item/list-item/list-item.component"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {ObjectId, getLodTypeDescription} from "@cm/template-nodes"
import {LocalPreviewRenderingService} from "@common/services/rendering/local-preview-rendering.service"
import {BlenderConnectorService} from "@app/template-editor/services/blender-connector.service"
import {ThreeTemplateSceneViewerComponent} from "@app/template-editor/components/three-template-scene-viewer/three-template-scene-viewer.component"
import {FilesService} from "@app/common/services/files/files.service"
import {ZoomButtonComponent} from "@app/template-editor/components/zoom-button/zoom-button.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-scene-viewer-options",
    imports: [MatMenuModule, ToggleComponent, MatButtonToggleModule, MatTooltipModule, ListItemComponent, ZoomButtonComponent],
    templateUrl: "./template-scene-viewer-options.component.html",
    styleUrl: "./template-scene-viewer-options.component.scss",
})
export class TemplateSceneViewerOptionsComponent {
    readonly sceneManagerService = inject(SceneManagerService)
    readonly threeSceneManagerService = inject(ThreeSceneManagerService)
    readonly localPreviewRenderingService = inject(LocalPreviewRenderingService)
    readonly blenderConnectorService = inject(BlenderConnectorService)

    readonly $selectedCameraId = input.required<ObjectId | undefined>({alias: "selectedCameraId"})
    readonly $threeViewer = input.required<ThreeTemplateSceneViewerComponent>({alias: "threeViewer"})
    readonly $fileTitle = input<string>(undefined, {alias: "fileTitle"})

    readonly selectedCameraIdChanged = output<ObjectId | undefined>()

    readonly $getLodTypeDescription = signal(getLodTypeDescription)

    constructor() {
        effect(() => {
            const selectedCameraId = this.$selectedCameraId()
            if (selectedCameraId) {
                const cameras = this.sceneManagerService.$cameras()
                if (!cameras.find((camera) => camera.id === selectedCameraId)) {
                    if (cameras.length > 0) this.selectedCameraIdChanged.emit(cameras[0].id)
                    else this.selectedCameraIdChanged.emit(undefined)
                }
            }
        })
    }

    async captureSnapshot() {
        const snapshot = await this.$threeViewer().captureSnapshot("image/png")
        FilesService.downloadFile(`${this.$fileTitle() || "snapshot"}.png`, snapshot)
    }

    protected labelFor<T>(options: {name: string; value: T}[], value: T): string {
        return options.find((option) => option.value === value)?.name ?? ""
    }
}
